// DownloadText.js
// Single source of truth for building & downloading the "Product Order Config" TXT.
// ESLint/Vercel-safe; no DOM leaks; compatible with both RetailModal & SizeCheckModal.

export function downloadProductOrderConfig({
  // summary / identity
  prod_id,
  prod_label,
  operation_id,
  operation_label,
  width_mm,
  height_mm,
  selectedOpIds = [],

  // default (required) config rows + values
  reqRows = [],            // [{config_id, config, options, def_value}]
  reqValues = {},          // { [config_id]: value }

  // ops config rows + values
  opsRows = [],            // [{config_id, config, options, def_value}]
  opsValues = {},          // { [config_id]: value }

  // retail visibility
  isPurchaser = false,
  srpCalc = null,          // RetailModal-calculated prices (optional in SizeCheck flow)

  // optional overrides
  filenameTemplate = "PRODORDCFG_{{prod_name}}_ops-{{op_label}}_{{ts}}.txt",
  now = new Date(),
}) {

  // --- NEW: Track Download Action ---
  try {
    fetch("/api/data?scope=usage", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ action: "download" }),
    }).catch(() => {}); 
  } catch (e) {
    // Ignore tracking errors
  }
  // ----------------------------------
  
  // ---------- helpers ----------
  const pad = (n) => String(n).padStart(2, "0");
  const ts = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}_` +
             `${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;

  const clean = (s) => String(s || "").trim();
  const sanitize = (s) => clean(s).replace(/[\\/:*?"<>|]/g, "_");
  const prodName = clean(prod_label).split("—")[0].trim();
  const opLabel  = clean(operation_label);

  const renderedFilename = filenameTemplate
    .replace("{{prod_name}}", sanitize(prodName))
    .replace("{{op_label}}", sanitize(opLabel))
    .replace("{{ts}}", ts);

  const num = (x) => {
    const n = Number(x);
    return Number.isFinite(n) ? n : 0;
  };
  const fmt = (n) => (Number.isFinite(n) ? n.toLocaleString("en-US") : "");

  // ---------- INSERTION POINT 1: DYNAMIC IMPORT TAX EVALUATION ENGINE ----------
  const pid = Number(prod_id);
  const operationalTextScan = Object.values(opsValues || {}).map(String).join(" ").toLowerCase();

  let solvedTaxRateLabel = "20% (Mặc định - Hệ thống tự động áp dụng)";
  let taxSystemAlertText = "";

  // Group A: Outdoor EVB & Aluminum Blades -> 15%
  if ([213, 199, 1323].includes(pid)) {
    solvedTaxRateLabel = "15% (Mành EVB ngoài trời / Mành lá nhôm)";
  } 
  // Group B: Roller, Duette, Pirouette, Silhouette -> 12%
  else if ([1945, 305, 306, 2635, 315, 2181, 2845, 533, 2238, 534, 2236, 2253, 76, 2235].includes(pid)) {
    solvedTaxRateLabel = "12% (Mành Roller / Duette / Pirouette / Silhouette)";
  } 
  // Group C: Wood & Monocom contextual split checks -> 27% vs 25%
  else if ([157, 2268].includes(pid)) {
    if (operationalTextScan.includes("faux")) {
      solvedTaxRateLabel = "27% (Mành Monocom giả gỗ - Faux Wood)";
    } else {
      solvedTaxRateLabel = "25% (Mành gỗ tự nhiên - Basswood)";
    }
  } 
  // Group D: Catch-all Fallback Default Configuration Note Trigger
  else {
    taxSystemAlertText = `⚠️ CHÚ Ý HỆ THỐNG: Mã sản phẩm [ID: ${pid}] chưa được cấu hình phân loại thuế chính thức. Hệ thống tự động áp dụng mức mặc định 20%.`;
  }
  // -----------------------------------------------------------------------------

  // ---------- gating / special cases (must match SizeCheckModal) ----------
  const opIdsForQuery = Array.isArray(selectedOpIds)
    ? selectedOpIds.map(Number).filter((n) => Number.isInteger(n) && n > 0)
    : (Number.isInteger(operation_id) ? [Number(operation_id)] : []);

  // Duolite gating (prod 2238 + op 1823 + Duolite chosen at config 2807)
  const CONFIG_2238_DUOLITE_GATED_DEFAULT_IDS = new Set([2385, 2389]);
  const CONFIG_2238_DUOLITE_TRIGGER_ID = 2807;
  const is2238WithOp1823 =
    pid === 2238 && opIdsForQuery.some((id) => id === 1823);
  const duoliteSelectedFor2807 =
    is2238WithOp1823 &&
    /\bduolite\b/i.test(String(opsValues?.[String(CONFIG_2238_DUOLITE_TRIGGER_ID)] || ""));

  const itemsDefault = (Array.isArray(reqRows) ? reqRows : []).map((r) => {
    const idNum = Number(r.config_id);
    let v = String(reqValues?.[String(r.config_id)] ?? "");
    if (duoliteSelectedFor2807 && CONFIG_2238_DUOLITE_GATED_DEFAULT_IDS.has(idNum)) {
      v = "Refer below";
    }
    return { section: "default", config_id: r.config_id, config: r.config, value: v };
  });

  const itemsOps = (Array.isArray(opsRows) ? opsRows : []).map((r) => ({
    section: "ops",
    config_id: r.config_id,
    config: r.config,
    value: String(opsValues?.[String(r.config_id)] ?? ""),
  }));

  if (pid === 1323) {
    itemsDefault.push({
      section: "default",
      config_id: 2389,
      config: "Config 2389 (hidden, mirrors 2385)",
      value: String(reqValues?.["2389"] ?? ""),
    });
  }

  const required_lines = itemsDefault.map((it) => `- ${it.config}: ${it.value ?? ""}`).join("\n");
  const ops_lines = itemsOps.map((it) => `- ${it.config}: ${it.value ?? ""}`).join("\n");

  const generated = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} ` +
                    `${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`;

  // ---------- INSERTION POINT 2: METADATA INTERPOLATION MAP MAP ----------
  const map = {
    prod_id,
    prod_label,
    operation_id,
    operation_label,
    width_mm: num(width_mm),
    height_mm: num(height_mm),
    generated,
    required_lines,
    ops_lines,
    import_tax_resolved: solvedTaxRateLabel, // Binds processed string asset
    ...(isPurchaser
      ? {
          srp_usd: fmt(srpCalc?.result?.usd),
          srp_vnd: fmt(srpCalc?.result?.vnd),
          fx_vnd_per_usd: fmt(srpCalc?.result?.fx_vnd_per_usd),
        }
      : {
          srp_vnd: fmt(srpCalc?.result?.vnd),
        }),
  };

  // ---------- INSERTION POINT 3: REPORT RENDERING MATRIX ASSEMBLER ----------
  const header = [
    "Product Order Config",
    "Generated: {{generated}}",
    "",
    "Product: {{prod_label}} (ID: {{prod_id}})",
    "Operation: {{operation_label}}",
    "",
    "Width (mm): {{width_mm}}",
    "Height (mm): {{height_mm}}",
    "",
    "Required product configuration:",
    "{{required_lines}}",
    "",
    "Operation-specific configuration (optional):",
    "{{ops_lines}}",
    "",
    ...(isPurchaser
      ? [
          "Retail price (calculated):",
          "- SRP (USD): {{srp_usd}}",
          "- SRP (VND): {{srp_vnd}}",
          "- FX used: {{fx_vnd_per_usd}} VND/USD",
          "- Thuế nhập khẩu VN ước tính: {{import_tax_resolved}}",
        ]
      : [
          "Retail price (calculated):",
          "- SRP (VND): {{srp_vnd}}",
          "- Thuế nhập khẩu VN ước tính: {{import_tax_resolved}}",
        ]),
  ];

  const hasEwp = typeof window !== "undefined"
    && window.EWP_EXPORT
    && typeof window.EWP_EXPORT.downloadTxt === "function";

  if (hasEwp) {
    window.EWP_EXPORT.downloadTxt(map, { filename: renderedFilename, header, baseLine: "", optionLine: "", totalLine: "" }, []);
    return;
  }

  const hasGenericTxt = typeof window !== "undefined"
    && typeof window.dl_prod_ord_config_txt === "function";

  const textFallbackSegments = [
    "Product Order Config",
    `Generated: ${map.generated}`,
    "",
    `Product: ${map.prod_label} (ID: ${map.prod_id})`,
    `Operation: ${map.operation_label}`,
    "",
    `Width (mm): ${map.width_mm}`,
    `Height (mm): ${map.height_mm}`,
    "",
    "Required product configuration:",
    required_lines,
    "",
    "Operation-specific configuration (optional):",
    ops_lines,
    "",
    ...(isPurchaser
      ? [
          "Retail price (calculated):",
          `- SRP (USD): ${map.srp_usd}`,
          `- SRP (VND): ${map.srp_vnd}`,
          `- FX used: ${map.fx_vnd_per_usd} VND/USD`,
          `- Thuế nhập khẩu VN ước tính: ${map.import_tax_resolved}`,
        ]
      : [
          "Retail price (calculated):",
          `- SRP (VND): ${map.srp_vnd}`,
          `- Thuế nhập khẩu VN ước tính: ${map.import_tax_resolved}`,
        ]),
    "",
  ];

  if (taxSystemAlertText) {
    textFallbackSegments.push("======================================================================================================");
    textFallbackSegments.push("⚠️ CHÚ Ý HỆ THỐNG (SYSTEM ALERTS)");
    textFallbackSegments.push("======================================================================================================");
    textFallbackSegments.push(taxSystemAlertText);
    textFallbackSegments.push("");
  }

  textFallbackSegments.push(
    "------------------------------------------------------------------------------------------------------",
    "📌 BẢNG TRA CỨU THỜI GIAN SẢN XUẤT TẠI NHÀ MÁY ",
    "⚠️ CẢNH BÁO QUAN TRỌNG CHO SALES: Toàn bộ thời gian dưới đây CHỈ LÀ THỜI GIAN LÀM HÀNG TẠI NHÀ MÁY, tuyệt đối CHƯA TÍNH THỜI GIAN VẬN CHUYỂN QUỐC TẾ về Việt Nam. Khi báo khách, anh em bắt buộc phải cộng thêm thời gian nghi le/máy bay và thông quan vào.",
    "",
    "1. EVB/ERB (Kadan)",
    "*Có sẵn nguyên vật liệu: 4 - 5 tuần",
    "*Hết nguyên vật liệu: 6 - 10 tuần",
    "",
    "2. Silhouette, Duette, RB (EOS/PV3), Monocom Ráp Tại Malaysia",
    "Nhà máy Malaysia chỉ trữ sẵn vài màu hot. Nếu hết màu khách chọn, họ phải order từ các nước khác theo thứ tự ưu tiên: Trung Quốc ➔ Đài Loan ➔ Mỹ ➔ Ấn Độ.",
    "",
    "*Sẵn Hàng: 1 tuần",
    "*Hết Hàng: 6 - 10 tuần",
    "",
    "3. Vải ETTE - Dệt Thẳng Tại Nhà Máy Mỹ (US)",
    "*Ít khi hụt vải, trừ mấy mã cổ lỗ sĩ họ bỏ mẫu.",
    "*Sẵn Hàng: 4 - 6 tuần",
    "",
    "4. Cửa Chớp Shutter - Nhà Máy Mexico",
    "*Sẵn Hàng: 6 - 8 tuần.",
    "",
    "5. Rèm Cuốn EOS RB tại Nhà Máy Trung Quốc",
    "*Sẵn Hàng: 4 tuần (dao động nhẹ tùy loại vải khách chọn).",
    "*Hết Hàng: 6 - 10 tuần",
    "",
    "6. Mành Sáo Nhôm Luxaflex (Nhà Máy Đài Loan - Bản 16/25/50mm)",
    "*Sẵn Hàng: 2 - 3 tuần",
    "*Hết Hàng: 6 - 10 tuần",
    "",
    "7. Hàng Ngoài Danh Mục (Các thương hiệu KHÔNG phải của HD)",
    "Đối với bất kỳ SP nào thuộc thương hiệu khác nhưng được thu mua hộ qua HDM: Thời gian sản xuất sẽ khác hoàn toàn và NẰM NGOÀI TẦM KIỂM SOÁT của HDM.",
    "",
    "Cách xử lý: Anh em sales bắt buộc phải hỏi thông tin riêng cho từng đơn ngay từ lần đầu tiên gửi yêu cầu (hỏi ca nào báo ca đó).",
    "------------------------------------------------------------------------------------------------------",
    ""
  );

  const textFallback = textFallbackSegments.join("\n");

  if (hasGenericTxt) {
    window.dl_prod_ord_config_txt({ filename: renderedFilename, text: textFallback });
    return;
  }

  const blob = new Blob([textFallback], { type: "text/plain;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = renderedFilename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}